import { snakeCase } from 'lodash'
import { usePaginationStore } from '@app/stores/tables/PaginationStore'
import { defineStore } from 'pinia'
import { reportError } from '@lib/errorHandler'

// These functions are used to compose a pinia store for a collection of model
// objects with an 'id' property, presumably fetched from our API.
// Example usage for a users collection:
//
// export const useUserStore = defineStore('UserStore', {
//  state: {
//    ...modelCollectionState('users'),
//    // additional state can be added here
//  },
//  actions: {
//    ...modelCollectionActions('users'),
//    // additional actions can be added here
//  }
// })

export function modelCollectionState(collectionName) {
  if (collectionName === undefined) {
    throw new Error('collectionName is required')
  }

  return { collectionName, [collectionName]: [], selectedIndex: null }
}

export function modelCollectionGetters(collectionName, singularName) {
  const suffix = collectionName.charAt(0).toUpperCase() + collectionName.slice(1) // ex: ContentAssets
  const suffixSingular = singularName
    ? singularName.charAt(0).toUpperCase() + singularName.slice(1)
    : suffix.slice(0, -1) // ex: ContentAsset

  return {
    /*
     * Get a single object from collection.
     *
     * If the collection only has 0-1 entries you can call without an id to get the single entry back,
     * this is a convenience for use in contexts like a show or edit page where you are just dealing with one object.
     *
     * param {number} id - The id of the object to retrieve.
     */
    ['get' + suffixSingular]: (state) => {
      return (id) => {
        const collection = state[collectionName]

        if (id === undefined) {
          if (collection.length > 1) {
            const error = new Error('Please pass an id argument when there are multiple entries')
            reportError(
              error,
              `get${suffixSingular}`,
              `${suffixSingular}Store (ModelCollectionStore)`,
            )
            throw error
          }
          return collection[0]
        }

        // We perform comparison in type-insensitive way. Mainly this is because ids are sometimes passed as strings
        // and sometimes as int in our Vue components.
        // Supports case where id param is null and we look up an object with null id,
        // which can exist in case of freshly initialized objects.
        return collection.find((obj) => obj?.id?.toString() === id?.toString())
      }
    },

    firstItemSelected() {
      const paginationStore = usePaginationStore()
      return this.selectedIndex === 0 && paginationStore.currentPage === 1
    },

    lastItemInCollectionSelected() {
      const paginationStore = usePaginationStore()
      return (
        this.selectedIndex === this[collectionName].length - 1 &&
        paginationStore.currentPage === paginationStore.totalPages
      )
    },

    getSelectedItem() {
      return this[collectionName][this.selectedIndex] || null
    },
  }
}

export function modelCollectionActions(collectionName, singularName) {
  if (collectionName === undefined) {
    throw new Error('collectionName is required')
  }

  if (collectionName.includes('_')) {
    throw new Error('collectionName must be camelCase')
  }
  // collectionName should be a plural such as 'users', and camel-case.
  // Currently we assume that it ends in s, some more sophisticated inflection logic will need to be
  // added to support other plural forms.
  // Based on collection name we'll generate standard API actions. For instance, if collectionName is users:
  //   - fetchUsers
  //   - createUser
  //   - updateUser
  //   - deleteUser
  // We assume a standard API structure where the response is an object with a key matching the collectionName,
  // and the standard API method naming convention is followed  for the collection
  // in api.js (e.g. getUsers, createuser, updateUser, deleteUser).

  // These constants help us construct different action names and API paths with the right inflection.
  // Note that special inflections are not supported, for instance we'll incorrectly calculate singluar of 'userProperties'
  // as 'userPropertie'. If you need specialized inflections please enhance to support.
  // Assuming collectionName is 'contentAssets':
  const collectionUnderscored = snakeCase(collectionName)
  const singular = singularName || collectionName.slice(0, -1) // ex: contentAsset
  const singularUnderscored = snakeCase(singular) // ex: content_asset
  const suffix = collectionName.charAt(0).toUpperCase() + collectionName.slice(1) // ex: ContentAssets
  const suffixSingular = singularName
    ? singularName.charAt(0).toUpperCase() + singularName.slice(1)
    : suffix.slice(0, -1) // ex: ContentAsset
  const upsertFunctionName = 'upsert' + suffixSingular // ex: upsertContentAsset
  const upsertCollectionName = 'upsert' + suffix // ex: upsertContentAssets
  const replaceFunctionName = 'replace' + suffix // ex: replaceContentAssets

  return {
    // Retrieve the collection from the API replace the current collection with the new one
    // Any arguments supplied to the fetch call will be passed directly to the api function.
    //
    // If the response has pagination data in our standard format, we'll update the pagination
    // store with it.
    async ['fetch' + suffix](...args) {
      return this.api['get' + suffix](...args).then((response) => {
        this[replaceFunctionName](response.data[collectionUnderscored])
        const paginationStore = usePaginationStore()
        if (response.data.pagination) {
          paginationStore.currentPage = response.data.pagination.current_page
          paginationStore.perPage = response.data.pagination.per_page
          paginationStore.totalRows = response.data.pagination.total
        }
        return response
      })
    },
    // Retrieve a single member from the server.
    // Add it to collection if collection does not contain an item with specified id.
    // If item with that id is present, replace it (in a reactivity-compatible fashion).
    // This is meant to support a drill-down pattern where we get abbreviated versions of
    // model objects via call to #index action (e.g. to populate a table), then as-needed
    // get the more detailed versions of objects via this call (e.g. to show record details in sidebar.)
    async ['fetch' + suffixSingular](id, ...args) {
      return this.api['get' + suffixSingular](id, ...args).then((response) => {
        this[upsertFunctionName](response.data[singularUnderscored])
        return response
      })
    },
    // Initialize a new (not save to DB yet) object
    async ['new' + suffixSingular](...args) {
      // We reset assuming that if we're doing a #new action, we're dealing with just one item in the store
      // This is a bit sketchy but is needed as long as we're shoehorning everything in a collection-oriented store
      this.$reset()
      return this.api['new' + suffixSingular](...args).then((response) => {
        this[upsertFunctionName](response.data[singularUnderscored])
        return response
      })
    },
    // Create a new member in the collection on the server and add it to the collection
    async ['create' + suffixSingular](member) {
      return this.api['create' + suffixSingular](member).then((response) => {
        this[upsertFunctionName](response.data[singularUnderscored])
        return response
      })
    },
    // Update a member in the collection on the server and update it in the collection
    async ['update' + suffixSingular](member) {
      return this.api['update' + suffixSingular](member).then((response) => {
        this[upsertFunctionName](response.data[singularUnderscored])
        return response
      })
    },
    // Just save whatever is in the store
    async ['save' + suffixSingular](id) {
      const member = this['get' + suffixSingular](id)
      return this.api['update' + suffixSingular](member).then((response) => {
        this[upsertFunctionName](response.data[singularUnderscored])
        return response
      })
    },
    // Delete a member in the collection on the server and remove it from the collection
    async ['delete' + suffixSingular](member) {
      return this.api['delete' + suffixSingular](member).then((response) => {
        this['remove' + suffixSingular](member)
        // Selections are invalidated when collection is updated
        this.deselectIndex()
        return response
      })
    },

    /*
     * Add an array to the collection if it wasn't present (when looked up by id), or update it if it was.
     * Done in a way that's compatible with Vue reactivity.
     *
     * param {array} members - The array of members to upsert.
     */
    [upsertCollectionName](members: Array) {
      members.forEach((member) => {
        this[upsertFunctionName](member)
      })
    },

    /*
     * Add a member to the collection if it wasn't present (when looked up by id), or update it if it was.
     * Done in a way that's compatible with Vue reactivity.
     *
     * param {object} member - The member to upsert.
     */
    [upsertFunctionName](member) {
      const collection = this[collectionName]
      const idx = collection.findIndex((m) => m.id === member.id)
      if (idx > -1) {
        collection.splice(idx, 1, member)
      } else {
        collection.push(member)
      }
      // Selections are invalidated when collection is updated
      this.deselectIndex()
    },

    /*
     * Remove a member from the collection
     * Done in a way that's compatible with Vue reactivity.
     *
     * param {object} member - The member to remove.
     */
    ['remove' + suffixSingular](member) {
      const collection = this[collectionName]
      const idx = collection.findIndex((m) => m.id === member.id)
      collection.splice(idx, 1)
    },

    /*
     * Replaces entire collection array.
     * Replacement is done in a way that's compatible with Vue reactivity system.
     *
     * param {Array} members - The new collection to replace the current one with.
     */
    [replaceFunctionName](members) {
      this[collectionName] = members
      // Go back to first item if an item was already selected
      if (this.selectedIndex) this.selectedIndex = 0
    },

    selectIndex(idx) {
      const collection = this[collectionName]
      if (idx >= 0 && idx < collection.length) {
        this.selectedIndex = idx
      }
    },

    deselectIndex() {
      this.selectedIndex = null
    },

    selectNext() {
      const paginationStore = usePaginationStore()

      if (this.selectedIndex !== null && this.selectedIndex === this[collectionName].length - 1) {
        paginationStore.nextPage()
      }

      if (this.selectedIndex !== null && this.selectedIndex < this[collectionName].length - 1)
        this.selectedIndex += 1
    },

    selectPrevious() {
      const paginationStore = usePaginationStore()

      if (this.selectedIndex !== null && this.selectedIndex === 0) {
        paginationStore.previousPage()
      }

      if (this.selectedIndex !== null && this.selectedIndex > 0) this.selectedIndex -= 1
    },
  }
}

/**
 * Helper function to create a new pinia ModelCollectionStore on a "on-demand" basis.
 * Usually used for defining stores that are not common enough to warrant a separate file.
 * @params {string} storeName - The name of the store to create.
 * @params {string} modelName - The name of the model to create the store for.
 * @returns a new pinia store for a Model.
 */
export function defineModelCollectionStore(storeName, modelName) {
  if (!storeName || !modelName) return

  return defineStore(storeName, {
    state: () => {
      return { ...modelCollectionState(modelName) }
    },
    getters: { ...modelCollectionGetters(modelName) },
    actions: { ...modelCollectionActions(modelName) },
  })
}
